import { useState, useEffect } from 'react'
import { client, urlFor } from '@/lib/sanity'
import type { Property, Agent } from '@/types'
import { mockProperties, mockAgents } from '@/mockData'

// Check if Sanity is properly configured
const isSanityConfigured = Boolean(import.meta.env.VITE_SANITY_PROJECT_ID)

// GROQ queries
const PROPERTIES_QUERY = `*[_type == "property"] | order(_createdAt desc) {
  _id,
  title,
  "slug": slug.current,
  price,
  address,
  city,
  state,
  zipCode,
  beds,
  baths,
  sqft,
  mainImage,
  images,
  "latitude": location.lat,
  "longitude": location.lng,
  description,
  propertyType,
  listingType,
  yearBuilt,
  lotSize,
  parking,
  features,
  "agent": agent->{
    _id,
    name,
    email,
    phone,
    photo,
    bio,
    specialties,
    yearsExperience,
    propertiesSold,
    rating,
    reviewCount,
    languages,
    serviceAreas
  },
  "nearbySchools": nearbySchools[]{
    distance,
    "school": school->{
      _id,
      name,
      type,
      rating,
      grades,
      studentCount,
      address
    }
  },
  hoaFees,
  listedAt,
  virtualTourUrl,
  openHouseDate,
  youtubeVideoUrls,
  walkScore,
  transitScore,
  bikeScore,
  neighborhoodData,
  condition,
  orientation,
  terraceArea,
  gatedCommunity,
  streetFrontage,
  layout,
  floor,
  totalFloors,
  petsAllowed,
  minimumContract,
  socialHousing,
  officeSuitable,
  isPenthouse,
  financingAvailable,
  fullBaths,
  halfBaths,
  heatingTypes,
  coolingTypes,
  appliances,
  laundryFeatures,
  interiorFeatures,
  livableArea,
  flooring,
  parkingFeatures,
  garageType,
  coveredSpaces,
  stories,
  patioAndPorch,
  fencing,
  lotFeatures,
  additionalStructures,
  propertySubType,
  architecturalStyle,
  constructionMaterials,
  foundation,
  roof,
  isNewConstruction,
  sold
}`

const PROPERTY_BY_ID_QUERY = `*[_type == "property" && _id == $id][0] {
  _id,
  title,
  "slug": slug.current,
  price,
  address,
  city,
  state,
  zipCode,
  beds,
  baths,
  sqft,
  mainImage,
  images,
  "latitude": location.lat,
  "longitude": location.lng,
  description,
  propertyType,
  listingType,
  yearBuilt,
  lotSize,
  parking,
  features,
  "agent": agent->{
    _id,
    name,
    email,
    phone,
    photo,
    bio,
    specialties,
    yearsExperience,
    propertiesSold,
    rating,
    reviewCount,
    languages,
    serviceAreas
  },
  "nearbySchools": nearbySchools[]{
    distance,
    "school": school->{
      _id,
      name,
      type,
      rating,
      grades,
      studentCount,
      address
    }
  },
  hoaFees,
  listedAt,
  virtualTourUrl,
  openHouseDate,
  youtubeVideoUrls,
  walkScore,
  transitScore,
  bikeScore,
  neighborhoodData,
  condition,
  orientation,
  terraceArea,
  gatedCommunity,
  streetFrontage,
  layout,
  floor,
  totalFloors,
  petsAllowed,
  minimumContract,
  socialHousing,
  officeSuitable,
  isPenthouse,
  financingAvailable,
  fullBaths,
  halfBaths,
  heatingTypes,
  coolingTypes,
  appliances,
  laundryFeatures,
  interiorFeatures,
  livableArea,
  flooring,
  parkingFeatures,
  garageType,
  coveredSpaces,
  stories,
  patioAndPorch,
  fencing,
  lotFeatures,
  additionalStructures,
  propertySubType,
  architecturalStyle,
  constructionMaterials,
  foundation,
  roof,
  isNewConstruction,
  sold
}`

const AGENTS_QUERY = `*[_type == "agent"] {
  _id,
  name,
  email,
  phone,
  photo,
  bio,
  specialties,
  yearsExperience,
  propertiesSold,
  rating,
  reviewCount,
  languages,
  serviceAreas
}`

interface SanityDocument {
  _id: string
  [key: string]: unknown
}

// School document from Sanity
interface SanitySchoolDoc {
  _id: string
  name?: { en: string; es: string }
  type?: 'Elementary' | 'Middle' | 'High'
  rating?: number
  grades?: string
  studentCount?: number
  address?: string
}

// Nearby school reference with distance
interface SanityNearbySchool {
  school?: SanitySchoolDoc
  distance?: number
}

interface SanityNeighborhoodData {
  medianHomeValue?: number
  medianRent?: number
  crimeRate?: 'Low' | 'Medium' | 'High'
  demographics?: {
    population?: number
    medianAge?: number
    medianIncome?: number
  }
}

interface SanityPropertyDoc extends SanityDocument {
  title: string | { en: string; es: string }
  slug: string
  price: number
  address: string
  city: string
  state: string
  zipCode: string
  beds: number
  baths: number
  sqft: number
  mainImage?: SanityImageSource
  images?: SanityImageSource[]
  latitude?: number
  longitude?: number
  description: string | { en: string; es: string }
  propertyType: string
  listingType: string
  yearBuilt?: number
  lotSize?: string
  parking?: number
  features?: (string | { en: string; es: string })[]
  agent?: SanityAgentDoc
  nearbySchools?: SanityNearbySchool[]
  hoaFees?: number
  listedAt?: string
  virtualTourUrl?: string
  openHouseDate?: string
  youtubeVideoUrls?: string[]
  walkScore?: number
  transitScore?: number
  bikeScore?: number
  neighborhoodData?: SanityNeighborhoodData
  // Extended property details
  condition?: string
  orientation?: string
  terraceArea?: number
  gatedCommunity?: string
  streetFrontage?: string
  layout?: string
  floor?: number
  totalFloors?: number
  petsAllowed?: string
  minimumContract?: string
  socialHousing?: string
  officeSuitable?: string
  isPenthouse?: boolean
  financingAvailable?: string
  // Interior facts
  fullBaths?: number
  halfBaths?: number
  heatingTypes?: (string | { en: string; es: string })[]
  coolingTypes?: (string | { en: string; es: string })[]
  appliances?: (string | { en: string; es: string })[]
  laundryFeatures?: string | { en: string; es: string }
  interiorFeatures?: (string | { en: string; es: string })[]
  livableArea?: number
  flooring?: (string | { en: string; es: string })[]
  // Property facts
  parkingFeatures?: (string | { en: string; es: string })[]
  garageType?: string
  coveredSpaces?: number
  stories?: number
  patioAndPorch?: (string | { en: string; es: string })[]
  fencing?: string | { en: string; es: string }
  lotFeatures?: (string | { en: string; es: string })[]
  additionalStructures?: (string | { en: string; es: string })[]
  // Construction facts
  propertySubType?: string | { en: string; es: string }
  architecturalStyle?: string | { en: string; es: string }
  constructionMaterials?: (string | { en: string; es: string })[]
  foundation?: string | { en: string; es: string }
  roof?: string | { en: string; es: string }
  isNewConstruction?: boolean
  sold?: boolean
}

interface SanityAgentDoc extends SanityDocument {
  name: string | { en: string; es: string }
  email: string
  phone: string
  photo?: SanityImageSource
  bio?: string | { en: string; es: string }
  specialties?: (string | { en: string; es: string })[]
  yearsExperience?: number
  propertiesSold?: number
  rating?: number
  reviewCount?: number
  languages?: string[]
  serviceAreas?: (string | { en: string; es: string })[]
}

interface SanityImageSource {
  _type: 'image'
  asset: {
    _ref: string
    _type: 'reference'
  }
}

// Helper to convert string or multilingual object to MultilingualText
function toMultilingualText(value: string | { en: string; es: string } | undefined, fallback = ''): { en: string; es: string } {
  if (!value) return { en: fallback, es: fallback }
  if (typeof value === 'string') return { en: value, es: value }
  return { en: value.en || fallback, es: value.es || fallback }
}

// Transform Sanity data to match our app types
function transformProperty(doc: SanityPropertyDoc): Property {
  // Transform schools from nearbySchools references
  const schools = (doc.nearbySchools || [])
    .filter(ns => ns.school) // Filter out any without a school reference
    .map((ns, index) => ({
      id: ns.school?._id || `school_${index}`,
      name: toMultilingualText(ns.school?.name),
      type: ns.school?.type || 'Elementary',
      rating: ns.school?.rating || 0,
      grades: ns.school?.grades || '',
      distance: ns.distance || 0, // Distance comes from the reference, not the school
      studentCount: ns.school?.studentCount || 0,
      address: ns.school?.address || '',
    }))

  return {
    id: doc._id,
    title: toMultilingualText(doc.title),
    price: doc.price,
    address: doc.address,
    city: doc.city,
    state: doc.state,
    zipCode: doc.zipCode,
    beds: doc.beds,
    baths: doc.baths,
    sqft: doc.sqft,
    mainImage: doc.mainImage ? urlFor(doc.mainImage).width(800).url() : '',
    images: (() => {
      console.log('Raw images data from Sanity:', doc.images);
      if (!doc.images || !Array.isArray(doc.images)) {
        console.log('Images field is missing or not an array, using mainImage as fallback');
        // If no images array, try to use mainImage as the only image
        if (doc.mainImage) {
          return [urlFor(doc.mainImage).width(800).url()];
        }
        return [];
      }

      // Process each image in the array
      const processedImages = doc.images.map((img, index) => {
        console.log(`Processing individual image ${index} in transformProperty:`, img);
        try {
          return urlFor(img).width(800).url();
        } catch (error) {
          console.error(`Error processing image ${index}:`, img, error);
          return '';
        }
      }).filter(url => url); // Remove any empty URLs

      console.log('Processed images array:', processedImages);
      return processedImages;
    })(),
    latitude: doc.latitude || 0,
    longitude: doc.longitude || 0,
    description: toMultilingualText(doc.description),
    propertyType: doc.propertyType,
    listingType: doc.listingType,
    yearBuilt: doc.yearBuilt || 0,
    lotSize: doc.lotSize || 'N/A',
    parking: doc.parking || 0,
    features: (doc.features || []).map(feature => toMultilingualText(feature)),
    agentId: doc.agent?._id || '',
    hoaFees: doc.hoaFees,
    daysOnMarket: doc.listedAt
      ? Math.floor((Date.now() - new Date(doc.listedAt).getTime()) / (1000 * 60 * 60 * 24))
      : 0,
    virtualTourUrl: doc.virtualTourUrl,
    openHouseDate: doc.openHouseDate,
    youtubeVideoUrls: doc.youtubeVideoUrls,
    neighborhoodData: {
      walkScore: doc.walkScore || 0,
      transitScore: doc.transitScore || 0,
      bikeScore: doc.bikeScore || 0,
      schools,
      medianHomeValue: doc.neighborhoodData?.medianHomeValue || 0,
      medianRent: doc.neighborhoodData?.medianRent || 0,
      crimeRate: doc.neighborhoodData?.crimeRate || 'Low',
      demographics: {
        population: doc.neighborhoodData?.demographics?.population || 0,
        medianAge: doc.neighborhoodData?.demographics?.medianAge || 0,
        medianIncome: doc.neighborhoodData?.demographics?.medianIncome || 0,
      },
    },
    // Extended property details
    condition: doc.condition,
    orientation: doc.orientation,
    terraceArea: doc.terraceArea,
    gatedCommunity: doc.gatedCommunity,
    streetFrontage: doc.streetFrontage,
    layout: doc.layout,
    floor: doc.floor,
    totalFloors: doc.totalFloors,
    petsAllowed: doc.petsAllowed,
    minimumContract: doc.minimumContract,
    socialHousing: doc.socialHousing,
    officeSuitable: doc.officeSuitable,
    isPenthouse: doc.isPenthouse,
    financingAvailable: doc.financingAvailable,
    // Interior facts
    fullBaths: doc.fullBaths,
    halfBaths: doc.halfBaths,
    heatingTypes: doc.heatingTypes?.map(item => toMultilingualText(item)),
    coolingTypes: doc.coolingTypes?.map(item => toMultilingualText(item)),
    appliances: doc.appliances?.map(item => toMultilingualText(item)),
    laundryFeatures: doc.laundryFeatures ? toMultilingualText(doc.laundryFeatures) : undefined,
    interiorFeatures: doc.interiorFeatures?.map(item => toMultilingualText(item)),
    livableArea: doc.livableArea,
    flooring: doc.flooring?.map(item => toMultilingualText(item)),
    // Property facts
    parkingFeatures: doc.parkingFeatures?.map(item => toMultilingualText(item)),
    garageType: doc.garageType,
    coveredSpaces: doc.coveredSpaces,
    stories: doc.stories,
    patioAndPorch: doc.patioAndPorch?.map(item => toMultilingualText(item)),
    fencing: doc.fencing ? toMultilingualText(doc.fencing) : undefined,
    lotFeatures: doc.lotFeatures?.map(item => toMultilingualText(item)),
    additionalStructures: doc.additionalStructures?.map(item => toMultilingualText(item)),
    // Construction facts
    propertySubType: doc.propertySubType ? toMultilingualText(doc.propertySubType) : undefined,
    architecturalStyle: doc.architecturalStyle ? toMultilingualText(doc.architecturalStyle) : undefined,
    constructionMaterials: doc.constructionMaterials?.map(item => toMultilingualText(item)),
    foundation: doc.foundation ? toMultilingualText(doc.foundation) : undefined,
    roof: doc.roof ? toMultilingualText(doc.roof) : undefined,
    isNewConstruction: doc.isNewConstruction,
    sold: doc.sold,
  }
}

function transformAgent(doc: SanityAgentDoc): Agent {
  return {
    id: doc._id,
    name: toMultilingualText(doc.name),
    email: doc.email,
    phone: doc.phone,
    photo: doc.photo ? urlFor(doc.photo).width(400).url() : '',
    bio: toMultilingualText(doc.bio),
    specialties: (doc.specialties || []).map(specialty => toMultilingualText(specialty)),
    yearsExperience: doc.yearsExperience || 0,
    propertiesSold: doc.propertiesSold || 0,
    rating: doc.rating || 0,
    reviewCount: doc.reviewCount || 0,
    languages: doc.languages || [],
    serviceAreas: (doc.serviceAreas || []).map(area => toMultilingualText(area)),
  }
}

export function useProperties() {
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // If Sanity is not configured, use mock data
    if (!isSanityConfigured) {
      console.warn('Sanity not configured, using mock data')
      setProperties(mockProperties)
      setLoading(false)
      return
    }

    client
      .fetch<SanityPropertyDoc[]>(PROPERTIES_QUERY)
      .then((data) => {
        const transformed = data.map(transformProperty)
        transformed.sort((a, b) => (a.sold ? 1 : 0) - (b.sold ? 1 : 0))
        setProperties(transformed)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to fetch properties, falling back to mock data:', err)
        setProperties(mockProperties)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  return { properties, loading, error }
}

export function useProperty(id: string) {
  const [property, setProperty] = useState<Property | null>(null)
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    console.log(`Fetching property with ID: ${id}`);

    // If Sanity is not configured, use mock data
    if (!isSanityConfigured) {
      console.warn('Sanity not configured, using mock data for property')
      const mockProperty = mockProperties.find(p => p.id === id)
      if (mockProperty) {
        console.log(`Found mock property with ID: ${id}`, mockProperty);
        setProperty(mockProperty)
        const mockAgent = mockAgents.find(a => a.id === mockProperty.agentId)
        if (mockAgent) {
          setAgent(mockAgent)
        }
      } else {
        console.warn(`Mock property with ID ${id} not found`);
      }
      setLoading(false)
      return
    }

    client
      .fetch<SanityPropertyDoc | null>(PROPERTY_BY_ID_QUERY, { id })
      .then((data) => {
        console.log(`Sanity query result for property ID ${id}:`, data);
        if (data) {
          console.log(`Raw Sanity data images:`, data.images);
          const transformedProperty = transformProperty(data);
          console.log(`Transformed property data:`, transformedProperty);
          console.log(`Transformed property images:`, transformedProperty.images);
          setProperty(transformedProperty)
          if (data.agent) {
            setAgent(transformAgent(data.agent))
          }
        } else {
          console.warn(`Property with ID ${id} not found in Sanity`);
          // Fallback to mock data if not found in Sanity
          const mockProperty = mockProperties.find(p => p.id === id)
          if (mockProperty) {
            console.log(`Found mock property as fallback with ID: ${id}`, mockProperty);
            console.log(`Mock property images:`, mockProperty.images);
            setProperty(mockProperty)
            const mockAgent = mockAgents.find(a => a.id === mockProperty.agentId)
            if (mockAgent) {
              setAgent(mockAgent)
            }
          } else {
            setProperty(null)
          }
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to fetch property, falling back to mock data:', err)
        const mockProperty = mockProperties.find(p => p.id === id)
        if (mockProperty) {
          setProperty(mockProperty)
          const mockAgent = mockAgents.find(a => a.id === mockProperty.agentId)
          if (mockAgent) {
            setAgent(mockAgent)
          }
        }
        setError(err.message)
        setLoading(false)
      })
  }, [id])

  return { property, agent, loading, error }
}

export function useAgents() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    client
      .fetch<SanityAgentDoc[]>(AGENTS_QUERY)
      .then((data) => {
        setAgents(data.map(transformAgent))
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to fetch agents:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  return { agents, loading, error }
}

export function useAgent(id: string) {
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const query = `*[_type == "agent" && _id == $id][0] {
      _id,
      name,
      email,
      phone,
      photo,
      bio,
      specialties,
      yearsExperience,
      propertiesSold,
      rating,
      reviewCount,
      languages,
      serviceAreas
    }`

    client
      .fetch<SanityAgentDoc | null>(query, { id })
      .then((data) => {
        setAgent(data ? transformAgent(data) : null)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to fetch agent:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [id])

  return { agent, loading, error }
}
